import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

/* DnD kit */
import { arrayMove } from '@dnd-kit/sortable';

/* Context */
import { useTripContext } from '../components/itinerary/TripContext';
import { useToast } from '../context/ToastContext';

/* Utils */
import { tripUtils } from '../utils/tripUtils';
import { formatDateOnly } from '../utils/dateUtils';

/* Hooks */
import { useReadOnlyToast } from './useReadOnlyToast';

export const useItineraryActions = (FIELD_OPTIONS) => {

    // Grab the data sets from our global context
    const {
        itineraryDays, setItineraryDays,
        defaultColumns, setDefaultColumns,
        fromDateObj, setToDateObj, setDurationInDays,
        structureVersion, setStructureVersion,
        reloadTrip,
        canEdit,
        setIsDirty
    } = useTripContext();

    const { id: tripId } = useParams();

    const { showToast } = useToast();
    const notifyReadOnly = useReadOnlyToast();

    // Days whose layout a dedicated column endpoint just persisted
    // The layout-sync watcher (in ItineraryBoard) consults this to skip a redundant updateDay for each.
    const columnSyncSkip = useRef(new Set());

    // Column editor
    const [nameConflict, setNameConflict] = useState({ visible: false, message: '' });

    // Context menu & actions
    const [contextMenu, setContextMenu] = useState(null);
    const [clipboard, setClipboard] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);

    // Context menu for itinerary rows
    const handleRowContextMenu = (e, dayIndex, rowId) => {
        if (!canEdit) return notifyReadOnly();
        e.preventDefault(); // Prevent default context menu
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            type: 'row',
            dayIndex,
            targetId: rowId
        });
    }

    // Context menu for itinerary columns
    const handleColumnContextMenu = (e, dayIndex, columnId, currentLabel) => {
        if (!canEdit) return notifyReadOnly();
        e.preventDefault();     // prevent default context menu
        e.stopPropagation();    // stop bubbling to prevent immediate dismissal by document listener
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            type: 'column',
            dayIndex,
            targetId: columnId,
            tempLabel: currentLabel // initialize with current label
        });
    }

    // Handle picking a type from the submenu
    const handleTypeSelect = (newType) => {
        setPendingAction({
            type: 'CHANGE_TYPE',
            payload: { ...contextMenu, newType }
        });
        setContextMenu(null);
    };

    // Helper to get dynamic text for the Scope Selection Modal
    const getScopeModalText = () => {
        if (!pendingAction) return { title: "Apply changes to...", message: "Would you like to apply this action to this day only, or all days in the itinerary?" };

        switch (pendingAction.type) {
            case 'ADD_COLUMN':
                return {
                    title: "Add New Column",
                    message: "Add this new column to this day only, or all days in the itinerary?"
                };
            case 'DELETE_COLUMN':
                return {
                    title: "Delete Column",
                    message: "Delete this column from this day only, or remove it from all days in the itinerary?"
                };
            case 'DUPLICATE_COLUMN':
                return {
                    title: "Duplicate Column",
                    message: "Duplicate this column for this day only, or all days in the itinerary?"
                };
            case 'PASTE_COLUMN':
                return {
                    title: "Paste Column",
                    message: "Paste column data into this day only, or all days in the itinerary?"
                };
            case 'RENAME_COLUMN':
                return {
                    title: "Rename Column",
                    message: `Rename column to "${pendingAction.payload.newName}" for this day only, or all days?`
                };
            case 'REORDER_COLUMN':
                return {
                    title: "Reorder Column",
                    message: "Reorder column for this day only, or all days in the itinerary?"
                };
            default:
                return {
                    title: "Apply changes to...",
                    message: "Apply this action to this day only, or all days in the itinerary?"
                };
        }
    };

    // Handle action initiation
    const initiateAction = (actionType, specificPayload = {}) => {
        if (!canEdit) return notifyReadOnly();
        // Prepare the payload based on current context
        const payload = { ...contextMenu, ...specificPayload };

        // Row operations execute immediately, no modal.
        if (['ADD_ROW', 'DELETE_ROW', 'DUPLICATE_ROW', 'PASTE_ROW', 'CLEAR_ROW'].includes(actionType)) {
            executeRowAction(actionType, payload);
            setContextMenu(null);
            return;
        }

        setPendingAction({ type: actionType, payload });
        setContextMenu(null); // Close context menu
    }

    // Execute action based on scope (SINGLE or ALL)
    const executeScopeAction = (scope) => {
        if (!canEdit) return notifyReadOnly();
        if (!pendingAction) return;

        const { type, payload } = pendingAction;
        const { dayIndex, targetId, activeId, overId, newIndex } = payload;

        setIsDirty(true);

        switch (type) {
            case 'ADD_COLUMN': {
                const label = prompt("Enter column name:");
                if (label && label.trim()) {
                    const newId = label.toLowerCase().replace(/\s+/g, '_');
                    const newColumn = { id: newId, label: label.trim(), width: 150, type: 'text', placeholder: 'Start typing...' };

                    if (scope === 'ALL') {
                        setDefaultColumns(prev => [...prev, newColumn]);
                    }

                    // Update days
                    setItineraryDays(prev => prev.map((day, idx) => {

                        // If Single: only update this day
                        if (scope === 'SINGLE' && idx !== dayIndex) return day;

                        // Avoid duplicates inside the specific day
                        if (day.columns.some(c => c.id === newId)) return day;

                        return { ...day, columns: [...day.columns, newColumn] };
                    }));
                }
                break;
            }

            case 'DELETE_COLUMN': {
                // Which days does this touch?
                const affectedDays = scope === 'ALL' ? itineraryDays : [itineraryDays[dayIndex]];

                // Guard (all-or-nothing, mirrors the server): never empty a day.
                const wouldEmpty = affectedDays.some(
                    d => d.columns.length <= 1 && d.columns.some(c => c.id === targetId)
                );
                if (wouldEmpty) {
                    showToast('At least one column must remain.', 'error');
                    setPendingAction(null);
                    return;
                }

                // Ids of days that actually hold this column (what the endpoint persists)
                const affectedDayIds = affectedDays
                    .filter(d => d.columns.some(c => c.id === targetId))
                    .map(d => d.id);

                if (scope === 'ALL') {
                    setDefaultColumns(prev => prev.filter(col => col.id !== targetId));
                }

                // Optimistic UI: drop the column + peel its value off every row
                setItineraryDays(prev => prev.map((day, idx) => {
                    if (scope === 'SINGLE' && idx !== dayIndex) return day;
                    return {
                        ...day,
                        columns: day.columns.filter(col => col.id !== targetId),
                        rows: day.rows.map(row => {
                            const { [targetId]: _removed, ...rest } = row;   // peel the dead column's value off every row
                            return rest;
                        })
                    };
                }));

                // Persist via the dedicated endpoint (edit mode only)
                // Owns the layout change, the item_data cleanup, AND the single structure_version bump
                if (tripId) {
                    // Tell the layout-sync watcher these days are already saved here
                    affectedDayIds.forEach(id => columnSyncSkip.current.add(id));
                    tripUtils.deleteColumn(tripId, {
                        columnId: targetId,
                        scope,
                        dayId: scope === 'SINGLE' ? itineraryDays[dayIndex].id : undefined
                    })
                        .then(res => { if (res?.structureVersion != null) setStructureVersion(res.structureVersion); })
                        .catch(() => showToast('Failed to delete column', 'error'));
                }
                break;
            }

            case 'DUPLICATE_COLUMN': {
                // Find definition from the target day
                const targetDay = itineraryDays[dayIndex];
                const colToDuplicate = targetDay.columns.find(c => c.id === targetId);

                if (colToDuplicate) {
                    const newColId = `${colToDuplicate.id}_copy_${crypto.randomUUID().slice(0, 4)}`;
                    const newColumn = { ...colToDuplicate, id: newColId, label: `${colToDuplicate.label} (Copy)` };

                    if (scope === 'ALL') {
                        setDefaultColumns(prev => {
                            const idx = prev.findIndex(c => c.id === targetId);
                            const newCols = [...prev];
                            if (idx !== -1) newCols.splice(idx + 1, 0, newColumn);
                            else newCols.push(newColumn);
                            return newCols;
                        });
                    }

                    setItineraryDays(prev => prev.map((day, idx) => {
                        const modifyStructure = (scope === 'ALL') || (scope === 'SINGLE' && idx === dayIndex);

                        if (!modifyStructure) return day;

                        // Add column definition
                        const colIdx = day.columns.findIndex(c => c.id === targetId);
                        let newColumns = [...day.columns];
                        if (colIdx !== -1) {
                            newColumns.splice(colIdx + 1, 0, newColumn);
                        } else {
                            newColumns.push(newColumn);
                        }

                        const newRows = day.rows.map(row => ({
                            ...row,
                            [newColId]: row[targetId] || ''
                        }));

                        return {
                            ...day,
                            columns: newColumns,
                            rows: newRows
                        };
                    }));
                }
                break;
            }

            case 'PASTE_COLUMN': {
                if (clipboard && clipboard.type === 'col') {
                    setItineraryDays(prev => prev.map((day, dIdx) => {
                        if (scope === 'SINGLE' && dIdx !== dayIndex) return day;

                        return {
                            ...day,
                            rows: day.rows.map((row, rIdx) => {
                                const match = clipboard.data.values.find(v => v.key === `${dIdx}-${rIdx}`);

                                if (match !== undefined) {
                                    return { ...row, [targetId]: match.value };
                                }
                                return row;
                            })
                        };
                    }));
                }
                break;
            }

            case 'CHANGE_TYPE': {
                const { newType } = payload;

                const typeOption = FIELD_OPTIONS.find(opt => opt.id === newType);
                const newPlaceholder = typeOption ? typeOption.defaultPlaceholder : '';

                // Days that actually hold this column (what the endpoint will touch)
                const affectedDays = scope === 'ALL' ? itineraryDays : [itineraryDays[dayIndex]];
                const affectedDayIds = affectedDays
                    .filter(d => d.columns.some(c => c.id === targetId))
                    .map(d => d.id);

                if (scope === 'ALL') {
                    setDefaultColumns(prev => prev.map(col =>
                        col.id === targetId ? { ...col, type: newType, placeholder: newPlaceholder } : col
                    ));
                }

                // Optimistic UI: change the column's type + reset its value on every row
                setItineraryDays(prev => prev.map((day, idx) => {
                    const modifyStructure = (scope === 'ALL') || (scope === 'SINGLE' && idx === dayIndex);

                    if (!modifyStructure) return day;

                    return {
                        ...day,
                        columns: day.columns.map(col =>
                            col.id === targetId ? { ...col, type: newType, placeholder: newPlaceholder } : col // Update column definitions
                        ),
                        rows: day.rows.map(row => ({
                            ...row,
                            [targetId]: '' // Reset data on type change
                        }))
                    };
                }));

                // Persist via the dedicated endpoint (edit mode only)
                if (tripId) {
                    // Tell the layout-sync watcher these days are already saved here
                    affectedDayIds.forEach(id => columnSyncSkip.current.add(id));
                    tripUtils.changeColumnType(tripId, {
                        columnId: targetId,
                        newType,
                        placeholder: newPlaceholder,
                        scope,
                        dayId: scope === 'SINGLE' ? itineraryDays[dayIndex].id : undefined
                    })
                        .then(res => { if (res?.structureVersion != null) setStructureVersion(res.structureVersion); })
                        .catch(() => showToast('Failed to change column type', 'error'));
                }
                break;
            }

            case 'RENAME_COLUMN': {
                const { newName } = payload;
                const cleanName = newName.trim();

                // Validation check function
                const checkConflict = (cols) => cols.some(c => c.label.toLowerCase() === cleanName.toLowerCase() && c.id !== targetId);

                if (scope === 'ALL') {
                    if (checkConflict(defaultColumns)) {
                        setNameConflict({ visible: true, message: `A column named "${cleanName}" already exists in the current layout.` });
                        setPendingAction(null); // Abort
                        return;
                    }
                    setDefaultColumns(prev => prev.map(col =>
                        col.id === targetId ? { ...col, label: cleanName } : col
                    ));
                }

                // Check conflicts for single day if needed
                if (scope === 'SINGLE') {
                    const targetDayCols = itineraryDays[dayIndex].columns;
                    if (checkConflict(targetDayCols)) {
                        setNameConflict({ visible: true, message: `A column named "${cleanName}" already exists on Day ${dayIndex + 1}.` });
                        setPendingAction(null); // Abort
                        return;
                    }
                }

                setItineraryDays(prev => prev.map((day, idx) => {
                    const modifyStructure = (scope === 'ALL') || (scope === 'SINGLE' && idx === dayIndex);

                    if (!modifyStructure) return day;

                    return {
                        ...day,
                        columns: day.columns.map(col =>
                            col.id === targetId ? { ...col, label: cleanName } : col
                        )
                    };
                }));
                break;
            }

            case 'REORDER_COLUMN': {
                if (scope === 'SINGLE') {
                    setItineraryDays(prev => {
                        const newDays = [...prev];
                        const dayToUpdate = newDays[dayIndex];

                        // Safety check: ensure indices are still valid
                        const currentOldIndex = dayToUpdate.columns.findIndex(c => c.id === activeId);
                        const currentNewIndex = dayToUpdate.columns.findIndex(c => c.id === overId);

                        if (currentOldIndex !== -1 && currentNewIndex !== -1) {
                            newDays[dayIndex] = {
                                ...dayToUpdate,
                                columns: arrayMove(dayToUpdate.columns, currentOldIndex, currentNewIndex)
                            };
                        }
                        return newDays;
                    });
                } else if (scope === 'ALL') {
                    setItineraryDays(prev => prev.map(day => {
                        // Try to find the same column in every day
                        const currentOldIndex = day.columns.findIndex(c => c.id === activeId);

                        // If this day doesn't have the column, skip it
                        if (currentOldIndex === -1) return day;

                        // Move it to the *same target index* if possible, or find where the "over" column is in this day.
                        let targetIndex = newIndex;
                        const overColIndex = day.columns.findIndex(c => c.id === overId);

                        // If we found the "anchor" column we dropped over, use its index
                        if (overColIndex !== -1) {
                            targetIndex = overColIndex;
                        }

                        return {
                            ...day,
                            columns: arrayMove(day.columns, currentOldIndex, targetIndex)
                        };
                    }));

                    // Also update the default columns template for future days
                    setDefaultColumns(prev => {
                        const currentOldIndex = prev.findIndex(c => c.id === activeId);
                        const overColIndex = prev.findIndex(c => c.id === overId);

                        if (currentOldIndex !== -1 && overColIndex !== -1) {
                            return arrayMove(prev, currentOldIndex, overColIndex);
                        }
                        return prev;
                    });
                }

                break;
            }

            default: break;
        }

        setPendingAction(null); // clear pending action
    }

    // Handle row action execution
    const executeRowAction = (type, payload) => {
        if (!canEdit) return notifyReadOnly();
        const { dayIndex, targetId } = payload;
        setIsDirty(true);

        const day = itineraryDays[dayIndex];
        const dayId = day.id;

        // Hoist any new rows OUT of setState so we know their ids for the API calls
        let newRow = null;
        if (type === 'ADD_ROW') {
            newRow = tripUtils.createEmptyRow(day.columns);
        } else if (type === 'DUPLICATE_ROW') {
            const src = day.rows.find(r => r.id === targetId);
            if (src) newRow = { ...src, id: crypto.randomUUID(), _version: 1 };
        } else if (type === 'PASTE_ROW' && clipboard?.type === 'row') {
            newRow = { ...clipboard.data, id: crypto.randomUUID(), _version: 1 };
        }

        let clearedRow = null;
        if (type === 'CLEAR_ROW') {
            const src = day.rows.find(r => r.id === targetId);
            clearedRow = { id: targetId, _version: src?._version };   // keep the version so the write isn't stale
            day.columns.forEach(col => { clearedRow[col.id] = ''; });
        }

        // Deleting the LAST row secretly creates a replacement — we need its id too
        let replacementRow = null;
        if (type === 'DELETE_ROW' && day.rows.filter(r => r.id !== targetId).length === 0) {
            replacementRow = tripUtils.createEmptyRow(day.columns);
        }

        // Compute the day's new order
        const currentIds = day.rows.map(r => r.id);
        let newOrderedIds = null;
        if (type === 'ADD_ROW' || type === 'PASTE_ROW') {
            if (newRow) newOrderedIds = [...currentIds, newRow.id];
        } else if (type === 'DUPLICATE_ROW' && newRow) {
            const i = currentIds.indexOf(targetId);
            newOrderedIds = [...currentIds];
            newOrderedIds.splice(i + 1, 0, newRow.id);        // same spot the UI inserts it
        } else if (type === 'DELETE_ROW') {
            newOrderedIds = replacementRow ? [replacementRow.id] : currentIds.filter(id => id !== targetId);
        }

        setItineraryDays(prev => prev.map((day, idx) => {
            if (idx !== dayIndex) return day;

            switch (type) {
                case 'ADD_ROW': {
                    return { ...day, rows: [...day.rows, newRow] };
                }
                case 'DELETE_ROW': {
                    const remaining = day.rows.filter((row) => row.id !== targetId);
                    return { ...day, rows: remaining.length ? remaining : [replacementRow] };
                }
                case 'DUPLICATE_ROW': {
                    if (!newRow) return day;
                    const rowIndex = day.rows.findIndex(r => r.id === targetId);
                    const newRows = [...day.rows];
                    newRows.splice(rowIndex + 1, 0, newRow);
                    return { ...day, rows: newRows };
                }
                case 'PASTE_ROW': {
                    return newRow ? { ...day, rows: [...day.rows, newRow] } : day;
                }
                case 'CLEAR_ROW': {
                    return { ...day, rows: day.rows.map(r => (r.id === targetId ? clearedRow : r)) };
                }
                default: {
                    return day;
                }
            }
        }));

        // ---- Autosave (edit mode only) ----
        if (tripId) {
            if (['ADD_ROW', 'DUPLICATE_ROW', 'PASTE_ROW'].includes(type)) {
                if (newRow) {
                    tripUtils.createItem(tripId, dayId, newRow)
                        .then(() => tripUtils.syncItemOrder(tripId, dayId, newOrderedIds))
                        .catch(() => showToast('Failed to save row', 'error'));
                }
            } else if (type === 'DELETE_ROW') {
                const ops = [tripUtils.deleteItem(tripId, targetId)];
                if (replacementRow) ops.push(tripUtils.createItem(tripId, dayId, replacementRow));
                Promise.all(ops)
                    .then(() => tripUtils.syncItemOrder(tripId, dayId, newOrderedIds))
                    .catch(() => showToast('Failed to delete row', 'error'));
            } else if (type === 'CLEAR_ROW') {
                tripUtils.updateItem(tripId, targetId, clearedRow, clearedRow._version, structureVersion)
                    .then(res => {
                        if (res?.version != null) {
                            setItineraryDays(prev => prev.map(d => d.id !== dayId ? d : {
                                ...d,
                                rows: d.rows.map(r => r.id === targetId ? { ...r, _version: res.version } : r)
                            }));
                        }
                    })
                    .catch(err => {
                        if (err?.message?.includes('conflict')) {
                            reloadTrip();
                            showToast('This trip was updated elsewhere — refreshed to the latest.', 'info');
                        } else {
                            showToast('Failed to clear row', 'error');
                        }
                    });
            }
        }
    }

    // Handle copying a row
    const performCopyRow = () => {
        const { dayIndex, targetId } = contextMenu;
        const day = itineraryDays[dayIndex];
        const row = day.rows.find(r => r.id === targetId);

        // Copy data to clipboard
        if (row) {
            setClipboard({ type: 'row', data: { ...row } });
        }
        // Close context menu
        setContextMenu(null);
    }

    // Handle copying a column
    const performCopyColumn = () => {
        const { dayIndex, targetId } = contextMenu;
        const day = itineraryDays[dayIndex];
        const col = day.columns.find(c => c.id === targetId);

        // Check if column exists
        if (!col) return;

        // Collect column data
        const columnData = [];
        itineraryDays.forEach((day, dIdx) => {
            day.rows.forEach((row, rIdx) => {
                columnData.push({ key: `${dIdx}-${rIdx}`, value: row[targetId] || '' });
            });
        });
        // Copy data to clipboard
        setClipboard({ type: 'col', data: { def: col, values: columnData } });
        // Close context menu
        setContextMenu(null);
    };

    // Delete a day
    const deleteDay = (dayIndex) => {
        if (!canEdit) return notifyReadOnly();

        // Guard: refuse if only one day remains (a trip needs ≥1 day)
        if (itineraryDays.length <= 1) {
            showToast('A trip must have at least one day.', 'error');
            return;
        }
        setIsDirty(true);

        const newCount = itineraryDays.length - 1;
        const deletedDayId = itineraryDays[dayIndex].id;
        const remaining = itineraryDays
            .filter((_, idx) => idx !== dayIndex)
            .map((day, idx) => {
                const date = new Date(fromDateObj); date.setDate(date.getDate() + idx);
                return { ...day, date };
            });

        // Drop the day, then re-flow every remaining date from fromDate
        setItineraryDays(remaining);

        // Shrink the trip by one day, anchored at fromDate
        setDurationInDays(newCount);
        const newEnd = new Date(fromDateObj);
        newEnd.setDate(newEnd.getDate() + (newCount - 1));
        setToDateObj(newEnd);

        if (tripId) {
            tripUtils.deleteDay(tripId, deletedDayId)
                .then(() => tripUtils.syncDayOrder(tripId, remaining.map(d => d.id)))
                .catch(() => showToast('Failed to delete day', 'error'));
        }
    };

    // Add a day
    const addDay = () => {
        if (!canEdit) return notifyReadOnly();
        if (!fromDateObj) return;
        setIsDirty(true);

        const newCount = itineraryDays.length + 1;
        const newDate = new Date(fromDateObj); newDate.setDate(newDate.getDate() + (newCount - 1));
        const newDay = {
            id: crypto.randomUUID(),
            date: newDate,
            rows: [tripUtils.createEmptyRow(defaultColumns)],
            columns: defaultColumns.map(c => ({ ...c })),
            note: ''
        };

        setItineraryDays(prev => [...prev, newDay]);
        setDurationInDays(newCount);
        setToDateObj(newDate);

        if (tripId) {
            tripUtils.createDay(tripId, {
                id: newDay.id, date: formatDateOnly(newDate),
                day_order: newCount, layout: newDay.columns, note: ''
            })
                .then(() => tripUtils.createItem(tripId, newDay.id, newDay.rows[0]))
                .catch(() => showToast('Failed to add day', 'error'));
        }
    };

    return {
        // Variables
        nameConflict, setNameConflict,
        contextMenu, setContextMenu,
        clipboard, setClipboard,
        pendingAction, setPendingAction,

        // Functions
        handleRowContextMenu, handleColumnContextMenu,
        handleTypeSelect, getScopeModalText,
        initiateAction, executeScopeAction, executeRowAction,
        performCopyRow, performCopyColumn,

        // Handler for day delete/add
        deleteDay, addDay,

        // Shared ref: days a dedicated column endpoint already persisted (layout-sync skips these)
        columnSyncSkip
    }
};